import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { format } from 'date-fns';
import { later } from 'lib/timers';
import { createSpendPoll } from 'lib/spend-poll';
import { createContinueFlow } from 'lib/ai-optimization';
import { notifyAiActivity, subscribeAiActivity } from 'lib/ai-activity';
import {
	CONTINUE_NOTICE_MS,
	SPEND_FAST_POLL_MS,
	SPEND_IDLE_POLL_MS,
	INITIAL_SPEND_STATE,
	INITIAL_LIVE_STATE,
	createInlineNotice,
	holdView,
	nextSpendState,
	nextLiveState,
	spendView,
	liveView,
	outcomeActivity,
} from 'lib/openai-spend';
import { continueAiOptimizations, getOpenAiSpend } from 'api/trading-api';
import { CircleExclamationIcon } from 'components/icons';
import InfoIconRed from 'components/common/InfoIconRed';

// Local time of the as-of tooltip; a bad string from the server gives '?' and never throws
const formatTime = iso => {
	const date = new Date(iso);
	return Number.isNaN(date.getTime()) ? '?' : format(date, 'HH:mm');
};

// Text colour of a live part when it is fresh; both turn grey when the figures could not be refreshed
const LIVE_TONES = { running: 'text-indigo-300', recent: 'text-gray-300' };

// What OpenAI costs today and this month, for the admin header, followed by the estimate of the AI runs that are running
// now. This component owns the poll (the header returns null before it mounts, so it cannot): one load on mount, then
// every 15 s while an AI run is queued or running and every 10 minutes otherwise, through createSpendPoll, released on
// unmount. A successful Start in this browser (lib/ai-activity) makes it fetch at once, and while the browser tab is
// hidden it does not poll at all (it fetches once as soon as the tab is visible again). An answer that arrives after
// unmount or after a newer request is dropped. Trouble with the Costs figures is shown as greyed amounts plus a yellow
// exclamation icon after the label with the reason on its own hover, never as an alert. The hover on the amounts tells
// only their age. The live parts have their own hover and are greyed only when their own refresh failed.
// While trading-api reports a funds hold (AI runs paused for lack of OpenAI funds) a red info icon takes the place of the
// yellow one, with a popover that gives the message, the counts, the strategies and a button that continues the runs
// through the same flow as the AI panel; its confirmation shows inline in that popover for a moment. The flow and the
// notice timer are created in an effect and live in state (this block keeps no refs), the notice is stopped on unmount.
const OpenAiSpendDisplay = ({ className = '' }) => {
	const [state, setState] = useState(INITIAL_SPEND_STATE);
	const [live, setLive] = useState(INITIAL_LIVE_STATE);
	const [notice, setNotice] = useState(null);
	const [continuing, setContinuing] = useState(false);
	const [tools, setTools] = useState(null);

	useEffect(() => {
		const poll = createSpendPoll({
			fetchSpend: getOpenAiSpend,
			onOutcome: outcome => {
				setState(previous => nextSpendState(previous, outcome));
				setLive(previous => nextLiveState(previous, outcome));
			},
			activityOf: outcomeActivity,
			schedule: later,
			fastMs: SPEND_FAST_POLL_MS,
			idleMs: SPEND_IDLE_POLL_MS,
			subscribe: subscribeAiActivity,
			doc: typeof document === 'undefined' ? null : document,
		});
		poll.start();
		return () => {
			poll.stop();
		};
	}, []);

	useEffect(() => {
		const inlineNotice = createInlineNotice({ schedule: later, durationMs: CONTINUE_NOTICE_MS, onChange: setNotice });
		const flow = createContinueFlow({ continueRequest: continueAiOptimizations, notify: notifyAiActivity });
		setTools({ inlineNotice, flow });
		return () => {
			inlineNotice.stop();
		};
	}, []);

	const onContinue = async () => {
		if (!tools) {
			return;
		}
		setContinuing(true);
		const outcome = await tools.flow.run();
		if (outcome) {
			setContinuing(false);
			tools.inlineNotice.show(outcome.text);
		}
	};

	const view = spendView(state, formatTime);
	const liveParts = liveView(live);
	const hold = holdView(live);

	let stateIcon;
	if (hold || notice) {
		stateIcon = (
			<InfoIconRed text={hold ? hold.iconText : notice} open={Boolean(notice)}>
				{hold ? (
					<>
						<span>{hold.message}</span>
						<span>{hold.countsText}</span>
						{hold.strategiesText ? <span>{hold.strategiesText}</span> : null}
						{notice ? (
							<span>{notice}</span>
						) : (
							<button
								type="button"
								className="mt-2 rounded bg-green-500 px-2 py-1 text-xs text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
								disabled={continuing}
								onClick={onContinue}
							>
								Continue AI runs
							</button>
						)}
					</>
				) : (
					// no hold left while the confirmation still shows: an empty child keeps the popover panel, which then holds the notice as its text
					<span className="hidden" />
				)}
			</InfoIconRed>
		);
	} else if (view.error) {
		stateIcon = (
			<span className="cursor-default" title={view.error} aria-label={view.error} role="img">
				<CircleExclamationIcon className="h-3 w-3 text-yellow-400" />
			</span>
		);
	}

	return (
		<div className={clsx('flex flex-col whitespace-nowrap cursor-default', className)}>
			<span className="flex items-center gap-1 text-xs text-gray-400 leading-none">
				{view.label}
				{stateIcon}
			</span>
			<span className="flex items-baseline">
				<span
					className={clsx('text-sm cursor-default', view.stale ? 'text-gray-500' : 'text-gray-300')}
					title={view.tooltip}
				>
					{view.text}
				</span>
				{[liveParts.running, liveParts.recent].map(part => {
					if (!part) {
						return null;
					}
					return (
						<React.Fragment key={part.key}>
							<span aria-hidden="true" className="mx-1 text-sm text-gray-500">
								·
							</span>
							<span
								className={clsx('text-sm cursor-default', liveParts.stale ? 'text-gray-500' : LIVE_TONES[part.key])}
								title={part.tooltip}
							>
								{part.text}
							</span>
						</React.Fragment>
					);
				})}
			</span>
		</div>
	);
};

OpenAiSpendDisplay.propTypes = {
	className: PropTypes.string,
};

export default OpenAiSpendDisplay;
